import { useEffect, useMemo, useRef, useState } from 'react';

interface WaitlistDeadlineCountdownProps {
  /** 서버가 LLAMAR 시 영속화한 절대 만료 시각. */
  expiresAt?: string | null;
  /** 응답 생성 시점의 서버 UTC 시각 — 고객 기기 시계 오차를 제거한다. */
  serverTimeUtc?: string | null;
  /** 0초 도달 시 서버 소유 상태를 즉시 다시 조회한다. */
  onExpired?: () => void;
}

interface CountdownTarget {
  id: string;
  deadlineKey: string;
  hasValidDeadline: boolean;
  initialRemainingMs: number;
  startedAtMonotonicMs: number;
}

interface CountdownTick {
  targetId: string;
  remainingSeconds: number;
}

const TICK_INTERVAL_MS = 250;

function parseTimestamp(value: string | null | undefined): number | null {
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function monotonicNow(): number {
  return performance.now();
}

function createCountdownTarget(
  expiresAt: string | null | undefined,
  serverTimeUtc: string | null | undefined,
): CountdownTarget {
  const deadlineMs = parseTimestamp(expiresAt);
  const serverTimeMs = parseTimestamp(serverTimeUtc);
  const referenceTimeMs = serverTimeMs ?? Date.now();

  return {
    id: JSON.stringify([expiresAt ?? null, serverTimeUtc ?? null]),
    deadlineKey: deadlineMs === null ? `invalid:${expiresAt ?? 'missing'}` : String(deadlineMs),
    hasValidDeadline: deadlineMs !== null,
    initialRemainingMs: deadlineMs === null ? 0 : Math.max(0, deadlineMs - referenceTimeMs),
    startedAtMonotonicMs: monotonicNow(),
  };
}

function remainingSecondsAt(target: CountdownTarget, currentMonotonicMs: number): number {
  const elapsedMs = Math.max(0, currentMonotonicMs - target.startedAtMonotonicMs);
  return Math.max(0, Math.ceil((target.initialRemainingMs - elapsedMs) / 1_000));
}

/**
 * 호출 만료 절대시각 기반 카운트다운.
 *
 * 서버 시각으로 최초 남은 시간을 계산하고 이후에는 단조 시계를 사용한다. 따라서 고객
 * 기기 시계가 틀리거나 실행 중 변경돼도 흔들리지 않으며, 탭이 백그라운드에 있는 동안
 * interval 이 지연돼도 복귀 즉시 실제 경과시간을 따라잡는다.
 */
export function WaitlistDeadlineCountdown({
  expiresAt,
  serverTimeUtc,
  onExpired,
}: WaitlistDeadlineCountdownProps) {
  const target = useMemo(
    () => createCountdownTarget(expiresAt, serverTimeUtc),
    [expiresAt, serverTimeUtc],
  );
  const [tick, setTick] = useState<CountdownTick>(() => ({
    targetId: target.id,
    remainingSeconds: remainingSecondsAt(target, target.startedAtMonotonicMs),
  }));
  const notifiedDeadlineRef = useRef<string | null>(null);

  useEffect(() => {
    const update = () => {
      const remainingSeconds = remainingSecondsAt(target, monotonicNow());
      setTick((current) => {
        if (current.targetId === target.id && current.remainingSeconds === remainingSeconds) {
          return current;
        }

        return { targetId: target.id, remainingSeconds };
      });
    };

    update();
    if (target.initialRemainingMs <= 0) {
      return;
    }

    const intervalId = window.setInterval(update, TICK_INTERVAL_MS);
    document.addEventListener('visibilitychange', update);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', update);
    };
  }, [target]);

  const remainingSeconds =
    tick.targetId === target.id
      ? tick.remainingSeconds
      : remainingSecondsAt(target, target.startedAtMonotonicMs);

  useEffect(() => {
    if (
      !target.hasValidDeadline ||
      remainingSeconds > 0 ||
      notifiedDeadlineRef.current === target.deadlineKey
    ) {
      return;
    }

    notifiedDeadlineRef.current = target.deadlineKey;
    onExpired?.();
  }, [onExpired, remainingSeconds, target.deadlineKey, target.hasValidDeadline]);

  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;

  return (
    <span
      className="text-[clamp(13px,3.2vw,15px)] font-bold text-foreground tabular-nums"
      data-testid="waitlist-call-countdown"
    >
      {target.hasValidDeadline ? `${minutes}:${seconds.toString().padStart(2, '0')}` : '--:--'}
    </span>
  );
}
